import axios from 'axios'

// Permitir override en tiempo de ejecución desde localStorage (útil si el backend se mueve de puerto)
let runtimeApi = null;
try {
  if (typeof window !== 'undefined') {
    const v = localStorage.getItem('VENTIO_API_URL');
    if (v && /^https?:\/\//i.test(v)) runtimeApi = v.replace(/\/$/, '');
  }
} catch (_) { }

// Use rutas relativas por defecto para que el dev server (CRA) pueda proxear a /api
// Esto facilita el uso de túneles/proxies y evita llamadas a localhost desde dispositivos remotos.
// Si REACT_APP_API_URL apunta a localhost pero el cliente no está en localhost
// (por ejemplo, accediendo desde otra máquina en la LAN), ignoramos esa variable
// para evitar que la app intente llamar a "localhost" en el equipo cliente.
let envApi = process.env.REACT_APP_API_URL || '';
try {
  if (typeof window !== 'undefined' && envApi && /localhost|127\.0\.0\.1/.test(envApi)) {
    const clientHost = window.location.hostname || '';
    if (clientHost && !/^(localhost|127\.0\.0\.1)$/.test(clientHost)) {
      // Cliente remoto: no usar la URL de entorno que apunta a localhost
      envApi = '';
    }
  }
} catch (_) { /* ignore */ }

// Normalize runtime/env API: ensure if a full origin is provided (e.g. http://localhost:5000)
// we append the expected API root `/api` so callers using relative paths like '/auth/login'
// resolve to e.g. http://localhost:5000/api/auth/login instead of http://localhost:5000/auth/login.
const normalizeApiRoot = (u) => {
  if (!u) return '';
  try {
    // If u already contains '/api' path segment at the end, keep as-is
    if (u.endsWith('/api')) return u.replace(/\/$/, '');
    // If u ends with a path different than '/', append '/api'
    // If it's just an origin like http://host:port, append '/api'
    return u.replace(/\/$/, '') + '/api';
  } catch (_) { return u; }
};

const baseURL = normalizeApiRoot(runtimeApi) || normalizeApiRoot(envApi) || "/api";

const httpRequest = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    "Accept": "application/json",
  },
})

// Interceptor para agregar token a todas las peticiones
httpRequest.interceptors.request.use(
  (config) => {
    // Obtener usuario del localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user && user.token) {
          config.headers['Authorization'] = `Bearer ${user.token}`;
        }

        // Pista de tenant para el backend (persistente entre logins)
        const tid = (user && (user.tenantId?._id || user.tenantId)) || null;
        if (tid) config.headers['X-Tenant'] = tid;
      } catch (error) {
        console.error('Error al parsear user de localStorage:', error);
      }
    }

    // Si no hay usuario (pantalla de login), intentar con última pista guardada
    // EXCEPTO para endpoints de autenticación para no forzar un tenant incorrecto
    if (!config.headers['X-Tenant']) {
      const url = String(config.url || '').toLowerCase();
      const isAuthEndpoint = (
        url.includes('/auth/login') ||
        url.includes('/auth/register') ||
        url.includes('/auth/signup-public') ||
        url.includes('/auth/verify-email') ||
        url.includes('/auth/forgot-password') ||
        url.includes('/auth/reset-password')
      );
      try {
        const forceTid = localStorage.getItem('VENTIO_FORCE_TENANT');
        const lastTid = localStorage.getItem('VENTIO_LAST_TENANT');
        if (isAuthEndpoint) {
          // En auth, solo respetar el FORZADO explícito para evitar mismatches al registrarse desde cero
          if (forceTid) config.headers['X-Tenant'] = forceTid;
        } else {
          const hint = forceTid || lastTid || null;
          if (hint) config.headers['X-Tenant'] = hint;
        }
      } catch (_) { }
    }

    // Solo establecer Content-Type si no está ya definido (para permitir multipart/form-data)
    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas de error
httpRequest.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Si el token es inválido o expiró, limpiar localStorage y recargar
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      const errorMessage = error.response.data?.message || '';
      const mustLogoutPatterns = [
        'Invalid token',
        'Token',
        'expired',
        'Usuario no asociado a ningún comercio',
        'Comercio no encontrado'
      ];
      if (mustLogoutPatterns.some(p => errorMessage.includes(p))) {
        try {
          console.warn('🔄 Token inválido o expirado, limpiando sesión...');
          localStorage.removeItem('user');
          localStorage.removeItem('cart');
          if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        } catch (_) { }
      }
    }
    // Si el login falla por pista de tenant incorrecta, limpiar pistas y permitir reintentar
    if (error.response && error.response.status === 400) {
      const msg = (error.response.data && error.response.data.message) || '';
      if (msg.includes('Usuario no encontrado en el comercio indicado') || msg.includes('Comercio no encontrado para el identificador indicado')) {
        try {
          localStorage.removeItem('VENTIO_LAST_TENANT');
          // No eliminar FORCE automáticamente para no sorprender; el usuario puede estar probando explícito
        } catch (_) { }
      }
    }
    // Fallback inteligente cuando el backend cambia de puerto (auto-retry localhost:5001..5005)
    // Ocurre cuando el servidor de backend reintenta por puerto ocupado
    try {
      const isNetwork = !error.response && (error.code === 'ERR_NETWORK' || /Network Error/i.test(String(error.message)));
      if (!isNetwork) return Promise.reject(error);

      const cfg0 = error.config || {};
      const absUrl = typeof cfg0.url === 'string' ? cfg0.url : '';
      const baseFromCfg = cfg0.baseURL || httpRequest.defaults.baseURL || '';

      // Detectar localhost + puerto en baseURL o en la URL absoluta
      const mBase = String(baseFromCfg).match(/^(http:\/\/)(localhost|127\.0\.0\.1):(\d+)(\/.*)$/i);
      const mAbs = String(absUrl).match(/^http:\/\/(localhost|127\.0\.0\.1):(\d+)(\/.*)$/i);

      // Bloque de Auto-Curación: Si fallamos con un puerto distinto al 5000 y tenemos un override guardado,
      // es muy probable que sea un puerto obsoleto (stale). Borrarlo y reintentar con el default (5000).
      if (typeof window !== 'undefined' && localStorage.getItem('VENTIO_API_URL')) {
        const portInUse = mBase ? mBase[3] : (mAbs ? mAbs[2] : null);
        if (portInUse && portInUse !== '5000') {
          console.warn(`⚠️ Network Error en puerto ${portInUse}. Eliminando VENTIO_API_URL obsoleto y reintentando default (5000).`);
          localStorage.removeItem('VENTIO_API_URL');

          // Restaurar default
          const defaultBase = normalizeApiRoot(envApi) || '/api'; // envApi viene de arriba (scope modulo)
          httpRequest.defaults.baseURL = defaultBase;

          const retryCfg = { ...cfg0 };
          retryCfg.baseURL = defaultBase;
          // Asegurar que la URL no sea absoluta apuntando al puerto viejo
          if (mAbs) {
            try {
              const u = new URL(absUrl);
              retryCfg.url = u.pathname + u.search; // convertir a relativa
            } catch (_) { }
          }
          return httpRequest.request(retryCfg);
        }
      }

      if (!mBase && !mAbs) return Promise.reject(error);

      // Datos comunes
      const proto = 'http://';
      const host = (mBase ? mBase[2] : mAbs[1]);
      const portStr = (mBase ? mBase[3] : mAbs[2]);
      const originalPort = Number(portStr);
      const restFromBase = mBase ? mBase[4] : ''; // ej '/api'

      // Si la URL es absoluta, extraer path y raíz ('/api')
      let urlPath = '';
      let apiRoot = restFromBase || '/api';
      try {
        if (mAbs) {
          const u = new URL(absUrl);
          urlPath = u.pathname + (u.search || '');
          const parts = u.pathname.split('/').filter(Boolean);
          if (parts.length > 0) apiRoot = '/' + parts[0];
        }
      } catch (_) { }

      // Si el cliente no está en la misma máquina que el backend (ej: accediendo desde otra
      // máquina en la LAN), evitar hacer "port hopping" que provoca muchos ERR_CONNECTION_REFUSED
      // y mala UX. Sólo intentar reintentos de puerto si el host detectado coincide con el host
      // del navegador (es decir, estamos desarrollando localmente en la misma máquina).
      try {
        if (typeof window !== 'undefined' && window.location && host && host.toLowerCase() === 'localhost') {
          const currentHost = window.location.hostname;
          if (currentHost && currentHost.toLowerCase() !== 'localhost' && currentHost !== host) {
            // No estamos en la misma máquina: no intentamos puertos alternativos
            console.debug('[httpRequest] saltando port-hopping porque el cliente no es localhost', { host, currentHost });
            return Promise.reject(error);
          }
        }
      } catch (_) { }

      // Limit how many alternate ports we try to avoid infinite noisy retries in Dev
      const maxAttempts = 3;
      // Avoid repeating port-hopping for the same origin in the session
      const hopFlagKey = `VENTIO_HOPPED_${host}:${originalPort}`;

      // Only attempt port-hopping if the developer explicitly set a runtime override
      // OR if both client and base URL are localhost (same machine development).
      try {
        const hasOverride = typeof window !== 'undefined' && !!localStorage.getItem('VENTIO_API_URL');
        const clientIsLocal = typeof window !== 'undefined' && window.location && /^localhost$/.test(window.location.hostname);
        const baseIsLocal = !!(mBase || mAbs);
        if (!hasOverride && !(clientIsLocal && baseIsLocal)) return Promise.reject(error);
      } catch (_) { return Promise.reject(error); }

      try { if (typeof window !== 'undefined' && localStorage.getItem(hopFlagKey)) return Promise.reject(error); } catch (_) { }
      for (let i = 1; i <= maxAttempts; i++) {
        const altPort = originalPort + i;
        const altBase = `${proto}${host}:${altPort}${apiRoot}`.replace(/\/$/, '');
        try {
          const newCfg = { ...cfg0 };
          // Forzar a usar el nuevo base + path relativo
          if (mAbs) {
            // Si la request original era absoluta, reconstruir la url con el nuevo origin
            newCfg.url = `${proto}${host}:${altPort}${urlPath}`;
            delete newCfg.baseURL;
          } else {
            // Si era relativa con baseURL, cambiamos baseURL
            newCfg.baseURL = altBase;
          }
          newCfg.headers = { ...(cfg0.headers || {}) };
          // Usar la misma instancia para conservar interceptores (headers, X-Tenant, etc.)

          // Small exponential/backoff delay before retrying to avoid flooding the host
          await new Promise((resolve) => setTimeout(resolve, i * 200));

          const resp = await httpRequest.request(newCfg);
          // Persistir nueva base para siguientes requests
          try { localStorage.setItem('VENTIO_API_URL', altBase); } catch (_) { }
          httpRequest.defaults.baseURL = altBase;
          // Mark that hop succeeded for this origin so we don't repeatedly probe next time
          try { if (typeof window !== 'undefined') localStorage.setItem(hopFlagKey, '1'); } catch (_) { }
          return resp;
        } catch (e) {
          const stillNetwork = !e.response && (e.code === 'ERR_NETWORK' || /Network Error/i.test(String(e.message)));
          if (!stillNetwork) throw e; // otro error, cortar
          // If this was the last attempt, set hop-flag so we don't retry repeatedly
          if (i === maxAttempts) {
            try { if (typeof window !== 'undefined') localStorage.setItem(hopFlagKey, '1'); } catch (_) { }
          }
        }
      }
    } catch (_) { }
    return Promise.reject(error);
  }
);

export default httpRequest
